import React, { useContext, useState } from "react";
import {
  DatePicker,
  DatePickerInput,
  InlineNotification,
  Modal,
  NotificationActionButton,
  Select,
  SelectItem,
  StructuredListBody,
  StructuredListCell,
  StructuredListHead,
  StructuredListRow,
  StructuredListWrapper,
  TextInput,
} from "carbon-components-react";
import moment from "moment";
import { ActionableEvent, ActionableEvents } from "@eave/shared/types/api/actionable-events";
import { ActionableEventContext } from "underwriter_dashboard/hurdle_editors/CashCloseSummary/ActionableEventProvider";
import { selectableCOECategories } from "underwriter_dashboard/shared";

const dateFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: "UTC",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

const dateToFormatted = (d: string): string => {
  if (!d) {
    return "";
  }
  return dateFormatter.format(new Date(d));
};

const ActionableEventNotification = () => {
  const { reload, updateLoanApp, closingDate, actionableEvents, ackActionableEvent } =
    useContext(ActionableEventContext);

  const [showCOEReasonModal, setShowCOEReasonModal] = useState<boolean>(false);
  const [actionableEvent, setActionableEvent] = useState<ActionableEvent | null>(null);
  const [closingDateChangeReason, setClosingDateChangeReason] = useState<string>("");
  const [closingDateChangeCategory, setClosingDateChangeCategory] = useState<string>("");
  const [newClosingDate, setNewClosingDate] = useState<string>(closingDate);

  const acknowledge = (shouldSendToHL: boolean) => {
    setShowCOEReasonModal(false);
    ackActionableEvent(actionableEvent.id, shouldSendToHL).then(() => {
      reload();
    });
  };

  const save = () => {
    updateLoanApp(newClosingDate, closingDateChangeReason, closingDateChangeCategory).then(() => {
      // COE is being updated, which will send a coe date change event to HL, so don't have to also send an acknowledgement to HL
      acknowledge(false);
    });
  };

  const actionableEventReadableName = (eventType: string): string => {
    const m = {
      [ActionableEvents.CashOfferPurchaseCOEDateChangeEvent]: "Cash Offer Purchase COE",
      [ActionableEvents.CashOfferSellCOEDateChangeEvent]: "Cash Offer Sell COE",
      [ActionableEvents.TradeInCOEDateChangeEvent]: "Trade In DR COE",
    };
    return m[eventType] || eventType;
  };

  const categoryForActionableEvent = (eventType: string): string => {
    const m = {
      [ActionableEvents.CashOfferPurchaseCOEDateChangeEvent]: "hlh_performance_cash_offer",
      [ActionableEvents.CashOfferSellCOEDateChangeEvent]: "hlh_performance_cash_offer",
      [ActionableEvents.TradeInCOEDateChangeEvent]: "hlh_performance_trade_in",
    };
    return m[eventType] || "";
  };

  const setClosingDate = e => {
    // using moment to format the date according to how the backend expects the value
    const newDate = e.length > 0 ? moment(e[0]).format("YYYY-MM-DD") : null;
    setNewClosingDate(newDate);
  };

  return (
    <div>
      <Modal
        open={showCOEReasonModal}
        modalHeading="Review Close of Escrow Date"
        primaryButtonText="Save"
        secondaryButtonText="Dismiss without updating COE"
        primaryButtonDisabled={closingDateChangeReason === "" || closingDateChangeCategory === ""}
        onBlur={() => setShowCOEReasonModal(false)}
        onRequestClose={() => setShowCOEReasonModal(false)}
        onSecondarySubmit={() => acknowledge(true)}
        onRequestSubmit={() => save()}
      >
        <StructuredListWrapper>
          <StructuredListHead>
            <StructuredListRow head />
          </StructuredListHead>
          <StructuredListBody>
            <StructuredListRow>
              <StructuredListCell>
                Previous {actionableEventReadableName(actionableEvent?.name)}
              </StructuredListCell>
              <StructuredListCell>
                {actionableEvent?.data?.previous_close_of_escrow
                  ? dateToFormatted(actionableEvent?.data?.previous_close_of_escrow)
                  : ""}
              </StructuredListCell>
            </StructuredListRow>
            <StructuredListRow>
              <StructuredListCell>
                New {actionableEventReadableName(actionableEvent?.name)}
              </StructuredListCell>
              <StructuredListCell>
                {actionableEvent?.data?.close_of_escrow
                  ? dateToFormatted(actionableEvent?.data?.close_of_escrow)
                  : ""}
              </StructuredListCell>
            </StructuredListRow>
            {actionableEvent?.data?.change_reason && [
              <StructuredListRow key="change_reason_category">
                <StructuredListCell>Change category</StructuredListCell>
                <StructuredListCell>
                  {actionableEvent?.data?.change_reason?.category}
                </StructuredListCell>
              </StructuredListRow>,
              <StructuredListRow key="change_reason_note">
                <StructuredListCell>Note</StructuredListCell>
                <StructuredListCell>
                  {actionableEvent?.data?.change_reason?.note}
                </StructuredListCell>
              </StructuredListRow>,
              <StructuredListRow key="change_reason_author">
                <StructuredListCell>Author</StructuredListCell>
                <StructuredListCell>
                  {actionableEvent?.data?.change_reason?.author}
                </StructuredListCell>
              </StructuredListRow>,
            ]}
            <StructuredListRow>
              <StructuredListCell>Current Mortgage COE</StructuredListCell>
              <StructuredListCell>{dateToFormatted(closingDate)}</StructuredListCell>
            </StructuredListRow>
          </StructuredListBody>
        </StructuredListWrapper>
        <DatePicker
          id="date-picker-closing-date"
          datePickerType="single"
          dateFormat="m/d/Y"
          style={{ marginBottom: "10px" }}
          value={dateToFormatted(newClosingDate)}
          onChange={e => setClosingDate(e)}
        >
          <DatePickerInput
            id="date-picker-closing-date-input-id"
            name="closing_date"
            iconDescription="Select a closing date"
            labelText="New Mortgage COE"
            autocomplete="off"
            placeholder="mm-dd-yyyy"
            type="text"
            size="sm"
          />
        </DatePicker>
        <Select
          light
          id="coe-date-change-category-select"
          name="coe-date-change-category-select"
          labelText="Category"
          placeholder="Select Category"
          value={closingDateChangeCategory}
          onChange={e => setClosingDateChangeCategory(e.target.value)}
        >
          <SelectItem text="Select a category" value="" />
          {Object.values(selectableCOECategories).map(c => (
            <SelectItem key={c.value} text={c.label} value={c.value} />
          ))}
        </Select>
        <TextInput
          id="coe-date-change-reason-text-input"
          value={closingDateChangeReason}
          labelText="Please enter the reason for changing the Close of Escrow Date"
          onChange={e => setClosingDateChangeReason(e.target.value)}
        />
      </Modal>
      <div>
        {actionableEvents.map(e => {
          return (
            <InlineNotification
              key={e.id}
              kind="warning"
              hideCloseButton
              title={`${actionableEventReadableName(e.name)} has been updated`}
              subtitle="The mortgage COE may need to be updated"
              actions={[
                <NotificationActionButton
                  key="review_changes"
                  onClick={() => {
                    setActionableEvent(e);
                    setClosingDateChangeCategory(categoryForActionableEvent(e.name));
                    setShowCOEReasonModal(true);
                  }}
                >
                  Review Changes
                </NotificationActionButton>,
              ]}
            />
          );
        })}
      </div>
    </div>
  );
};

export default ActionableEventNotification;
